'use client';

import React, { useEffect } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';
import { useInView } from 'react-intersection-observer';

import styles from './styles.module.css';

import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ModuleProductsSkeleton } from '@/components/ModuleProductsSkeleton';
import { ProductImpressionHandler } from '@/components/ProductImpressionHandler';
import { RecentlyViewedModal } from '@/components/RecentlyViewed/RecentlyViewedModal';
import { RecentlyViewedProductCard } from '@/components/RecentlyViewed/RecentlyViewedProductCard';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { ImpressionsBatcherProvider } from '@/modules/impressions/ImpressionsBatcherProvider';
import { ModuleName } from '@/modules/impressions/types';
import { MAX_PRODUCT_CARDS } from '@/modules/recentlyViewed/constants';
import { getProductCardColumns } from '@/modules/recentlyViewed/helpers.ts';
import { useRecentlyViewed } from '@/modules/recentlyViewed/hooks/useRecentlyViewed';
import { useRecentlyViewedTracking } from '@/modules/recentlyViewed/hooks/useRecentlyViewedTracking';

type Props = {
  productId?: number;
  minimumProductsToRender?: number;
  moduleOrigin?: string;
  moduleHeaderSize?: ModuleHeaderSize;
} & React.HTMLAttributes<HTMLDivElement>;

export function RecentlyViewed({
  title,
  productId,
  minimumProductsToRender = 0,
  moduleOrigin,
  moduleHeaderSize = ModuleHeaderSize.Small,
  ...rest
}: Props) {
  const tCommon = useTranslations('common');

  const {
    hasClearedProducts,
    isClearAllVisible,
    onModuleCtaClick,
    onModalClearAllClick,
    products,
    isError,
    isLoading,
    isModalOpen,
    setIsModalOpen,
  } = useRecentlyViewed(productId);
  const { sendModuleClickAction, sendModuleView } =
    useRecentlyViewedTracking(productId);
  const { ref: sectionRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });
  const visibleProducts = products.slice(0, MAX_PRODUCT_CARDS);
  const hasProducts = products.length > 0;
  const hasMinimumProductsRequirement =
    products.length >= minimumProductsToRender;
  const showModuleCta = hasMinimumProductsRequirement && !isLoading && !isError;
  const placeholderColumns = getProductCardColumns(MAX_PRODUCT_CARDS);

  useEffect(() => {
    if (inView) {
      sendModuleView();
    }
  }, [inView]);

  function getShouldRender() {
    if (isLoading) {
      return true;
    }
    if (hasClearedProducts) {
      return false;
    }
    return hasProducts && hasMinimumProductsRequirement;
  }

  if (!getShouldRender()) {
    return <></>;
  }

  return (
    <ImpressionsBatcherProvider
      batchLocationId="recentlyViewed"
      impressionsType="productImpressions"
      eventType={ActivityTrackerEventType.PRODUCT_IMPRESSIONS}
      batchLimit={visibleProducts.length}
    >
      <section {...rest} ref={sectionRef}>
        <ModuleHeader title={tCommon('RecentlyViewed')} size={moduleHeaderSize}>
          {showModuleCta && (
            <button className={styles.ctaButton} onClick={onModuleCtaClick}>
              <Body variant="sm-regular" as="span">
                {isClearAllVisible ? tCommon('ClearAll') : tCommon('SeeMore')}
              </Body>
            </button>
          )}
        </ModuleHeader>
        {isLoading && (
          <ModuleProductsSkeleton
            minItemWidth={144}
            itemsPerRow={placeholderColumns}
            productsCount={placeholderColumns}
          />
        )}
        {hasProducts && (
          <ScrollableItemRow
            minItemWidth={144}
            itemsPerRow={getProductCardColumns(products.length)}
          >
            {visibleProducts.map((product, index) => (
              <li key={product.slug} className={styles.productListItem}>
                <ProductImpressionHandler
                  index={index}
                  isLastItem={index === visibleProducts.length - 1}
                  pageNumber={1}
                  moduleName={ModuleName.RecentlyViewed}
                >
                  <RecentlyViewedProductCard
                    id={product.id}
                    slug={product.slug}
                    pricing={{
                      ...product.pricing,
                      currency: product.pricing.currency_name,
                    }}
                    sizes={product.sizes}
                    pictures={product.pictures}
                    preview={product.preview}
                    brandName={product.brand_name}
                    hasVideo={product.has_video}
                    isLiked={product.is_liked}
                    handleClick={() =>
                      sendModuleClickAction(product.id, product.slug, index)
                    }
                    moduleOrigin={moduleOrigin}
                  />
                </ProductImpressionHandler>
              </li>
            ))}
          </ScrollableItemRow>
        )}
        <RecentlyViewedModal
          isOpen={isModalOpen}
          setOpen={setIsModalOpen}
          title={tCommon('RecentlyViewed')}
          onClearAllClick={onModalClearAllClick}
          productId={productId}
          moduleOrigin={moduleOrigin}
        />
      </section>
    </ImpressionsBatcherProvider>
  );
}
